import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../component-lib/Molecules/button/Button";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import SearchSelectDropdown from "../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import EventEHRConfirmationDialog from "../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import CreateEditDocumentDialog from "./dialogs/CreateEditDocumentDialog";
import ViewDocumentDialog from "./dialogs/ViewDocumentDialog";
import { searchIcon, plusIcon } from "../../assets";
import { componentKey, setCloseDocumentDialog } from "./documentsSlice";
import {
  createDocument,
  deleteDocument,
  getDocumentList,
  updateDocument,
} from "./documentsSaga";

interface CreateDocumentPayload {
  file: string;
  name: string;
  description: string;
}

const DocumentsScreen = () => {
  const { documentListData, closeDocumentDialog } = useSelector(
    (state: any) => state[componentKey],
  );
  const dispatch = useDispatch();
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [searchedValue, setSearchedValue] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [defaultRow, setDefaultRow] = useState<any>({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewDialog, setViewDialog] = useState<{
    open: boolean;
    url: string;
    name: string;
    type: string;
  }>({ open: false, url: "", name: "", type: "" });

  const listPayload = {
    pageNumber,
    limit: rowPerPage,
    name: searchedValue?.trim(),
  };

  const getDocuments = () => {
    dispatch(getDocumentList(listPayload));
  };

  useEffect(() => {
    getDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedValue, pageNumber, rowPerPage]);

  // Close the dialog once a create/update succeeds, then refetch.
  useEffect(() => {
    if (closeDocumentDialog) {
      setDialogState(null);
      setDefaultRow({});
      dispatch(setCloseDocumentDialog(false));
      getDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDocumentDialog]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPageNumber(1);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const handleDialogSubmit = (payload: CreateDocumentPayload) => {
    if (dialogState === "edit") {
      dispatch(updateDocument({ id: defaultRow?.id, apiPayload: payload }));
    } else {
      dispatch(createDocument(payload));
    }
  };

  const columns = [
    {
      field: "name",
      label: <div>Document Name</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.name || "-"}</p>
      ),
    },
    {
      field: "note",
      label: <div>Description</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.note || "-"}</p>
      ),
    },
    {
      field: "source",
      label: <div>Source</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.source || "-"}</p>
      ),
    },
    {
      field: "createdBy",
      label: <div>Created By</div>,
      renderLogic: (row: any) => {
        const creator = row?.adminCreator || row?.creator;
        const name = creator
          ? `${creator?.firstName || ""} ${creator?.lastName || ""}`.trim()
          : "";
        return <p className="font-normal">{name || "-"}</p>;
      },
    },
    {
      field: "createdAt",
      label: <div>Created At</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">
          {row?.createdAt ? moment(row?.createdAt).format("MM/DD/YYYY") : "-"}
        </p>
      ),
    },
    {
      field: "action",
      label: <div>Actions</div>,
      renderLogic: (row: any) => {
        const actionList = [
          {
            label: "View",
            onClickCb: () => {
              if (row?.file) {
                setViewDialog({
                  open: true,
                  url: row.file,
                  name: row?.name || "Document",
                  type: row?.type || "",
                });
              }
            },
            disabled: !row?.file,
          },
          {
            label: "Edit",
            onClickCb: () => {
              setDefaultRow(row);
              setDialogState("edit");
            },
          },
          {
            label: "Delete",
            onClickCb: () => {
              setSelectedId(row?.id);
              setOpenConfirmDialog(true);
            },
          },
        ];
        return (
          <div className="flex items-center">
            <MenuDropdown options={actionList} />
          </div>
        );
      },
    },
  ];

  const rows = Array.isArray(documentListData?.data)
    ? documentListData.data
    : [];

  return (
    <WrapperCard customClasses="m-3 p-3 flex-1 min-h-0">
      <div className="flex flex-col h-full gap-3">
        {/* HEADER ROW */}
        <div className="flex items-center justify-between w-full">
          <Heading title="Documents" customStyle={{ color: "#1D1D1D" }} />
          <div className="flex flex-wrap items-start gap-3">
            <SearchSelectDropdown
              freeSolo={true}
              searchedValue={searchedValue}
              onSearchValueChangeCb={handleSearchChange}
              size="small"
              label="Search By Document Name"
              fullWidth="300px"
              startIcon={
                <img
                  alt="search-icon"
                  src={searchIcon}
                  style={{ width: "20px", height: "20px" }}
                />
              }
            />
            <Button
              label="Add Document"
              variant="contained"
              startIcon={<img src={plusIcon} alt="add" />}
              onClickCb={() => {
                setDefaultRow({});
                setDialogState("add");
              }}
            />
          </div>
        </div>

        {/* DOCUMENT LIST */}
        <div style={{ overflow: "auto" }} className="flex-1">
          <Table
            columns={columns}
            rows={rows}
            noDataFoundMessage="No documents found!"
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: documentListData?.totalPages,
              totalCount: documentListData?.totalRecords,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: number) => setPageNumber(page),
              handleChangeRowsPerPage: (rows: string) => {
                setRowPerPage(rows);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {/* CREATE / EDIT DIALOG */}
      {dialogState && (
        <CreateEditDocumentDialog
          isOpen={!!dialogState}
          mode={dialogState}
          defaultData={defaultRow}
          handleClose={() => {
            setDialogState(null);
            setDefaultRow({});
          }}
          handleSubmit={handleDialogSubmit}
        />
      )}

      {/* VIEW DOCUMENT PREVIEW */}
      {viewDialog.open && (
        <ViewDocumentDialog
          isOpen={viewDialog.open}
          handleClose={() =>
            setViewDialog({ open: false, url: "", name: "", type: "" })
          }
          documentName={viewDialog.name}
          fileUrl={viewDialog.url}
          fileType={viewDialog.type}
        />
      )}

      {/* DELETE CONFIRMATION */}
      {openConfirmDialog && (
        <EventEHRConfirmationDialog
          open={openConfirmDialog}
          alertTitle="Delete Document"
          alertDescription="Are you sure you want to delete this document? This action cannot be undone."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          handleClose={(action) => {
            if (action === "submit" && selectedId) {
              dispatch(deleteDocument({ id: selectedId, listPayload }));
            }
            setOpenConfirmDialog(false);
            setSelectedId(null);
          }}
        />
      )}
    </WrapperCard>
  );
};

export default DocumentsScreen;
